import { formatCount } from "@nexus/ui";
import { WidgetSummary, WidgetFooter, WidgetIssueState } from "@nexus/widget-sdk";
import { RankingList, RANKING_ROW, RANKING_META, RANKING_VIZ } from "@nexus/widget-kit";
import { memo, useCallback, useId, useMemo, useRef } from "react";
import {
  defineLabels,
  firstError,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetState,
  SeverityChip,
} from "@nexus/widget-sdk";
import type { SeverityLevel } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import type {
  DetectionPerDomainConfig,
  DetectionPerDomainData,
  DetectionPerDomainEntry,
  DetectionTrendPoint,
} from "./types";

export type {
  DetectionPerDomainConfig,
  DetectionPerDomainData,
  DetectionPerDomainEntry,
  DetectionTrendPoint,
} from "./types";

export const LABELS = defineLabels([
  { key: "emptyTitle", name: "Empty state title", default: "No detections" },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "No detection data for the selected time range.",
  },
  { key: "summaryLabel", name: "Summary caption", default: "detections" },
  {
    key: "summaryDescription",
    name: "Summary description",
    default: "Select a domain to filter the dashboard",
  },
  { key: "domainHeading", name: "Domain column heading", default: "Domain" },
  {
    key: "detectionsHeading",
    name: "Detections column heading",
    default: "Detections",
  },
  { key: "trendHeading", name: "Trend column heading", default: "Trend" },
  {
    key: "actionableCaption",
    name: "Actionable count caption",
    default: "actionable",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "detection-per-domain",
  name: "Detection per Domain",
  description: "Detections grouped by domain. Click a domain row to filter the dashboard.",
  version: "1.1.0",
  labels: LABELS,
  dataRequests: {
    domains: {
      source: "detection-per-domain",
      contract: { name: "detection-per-domain", version: 1 },
      configurable: false,
      filterOutputs: {
        domain: {
          label: "Domain",
          field: "detection_domain",
          operator: "eq",
        },
      },
    },
  },
};

export const defaultConfig: DetectionPerDomainConfig = {
  title: "Detection per Domain",
  maxDomains: 10,
};

const EMPTY_DOMAINS: DetectionPerDomainEntry[] = [];

// ── Helpers ─────────────────────────────────────────────────────────────────

function actionablePct(total: number, actionable: number): number {
  if (total <= 0) return 0;
  return Math.round(Math.max(0, Math.min(100, (actionable / total) * 100)));
}

type DomainStatus = "nominal" | "degraded" | "critical" | null;

function getStatus(total: number, expected: number | null): DomainStatus {
  const ref = expected && expected > 0 ? expected : null;

  if (ref === null) return null;
  if (total > ref * 1.5) return "critical";
  if (total > ref) return "degraded";

  return "nominal";
}

function getDeltaPct(total: number, expected: number | null): number | null {
  if (!expected || expected <= 0) return null;
  return Math.round(((total - expected) / expected) * 100);
}

function formatSignedPct(value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${Math.abs(value)}%`;
}

// Domain health → SeverityChip level.
// 'critical' domain (overload) → severity critical (solid fill, loud-fill rule).
// 'degraded' domain → severity medium (amber dot, quiet).
// 'nominal' shows no chip (healthy domain = calm at rest).
const DOMAIN_SEV_LEVEL: Record<"degraded" | "critical", SeverityLevel> = {
  degraded: "medium",
  critical: "critical",
};

// Sparkline color: nominal → subtle neutral; degraded/critical → matching sev token.
// Status tokens removed — volume health is expressed via severity scale, not status scale.
const SPARKLINE_COLOR: Record<NonNullable<DomainStatus>, string> = {
  nominal: "text-muted",
  degraded: "text-sev-medium",
  critical: "text-sev-critical",
};

// DeltaText tone follows domain health state via sev tokens.
const DELTA_TONE: Record<NonNullable<DomainStatus>, string> = {
  nominal: "text-muted",
  degraded: "text-sev-medium",
  critical: "text-sev-critical",
};

// ── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: DomainStatus }) {
  if (status === null || status === "nominal") return null;
  return <SeverityChip level={DOMAIN_SEV_LEVEL[status]} />;
}

function TrendIcon({ up }: { up: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="h-3 w-3 shrink-0"
    >
      {up ? (
        <>
          <path d="M16 7h6v6" />
          <path d="m22 7-8.5 8.5-5-5L2 17" />
        </>
      ) : (
        <>
          <path d="M16 17h6v-6" />
          <path d="m22 17-8.5-8.5-5 5L2 7" />
        </>
      )}
    </svg>
  );
}

function DeltaText({ delta, status }: { delta: number | null; status: DomainStatus }) {
  if (delta === null) return null;
  // Tone follows domain health state, not arithmetic sign.
  const tone = status ? DELTA_TONE[status] : "text-muted";
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 font-medium tabular-nums ${tone}`}>
      <TrendIcon up={delta >= 0} />
      {formatSignedPct(delta)} vs baseline
    </span>
  );
}

const SPARK_W = 104;
const SPARK_H = 34;
const SPARK_PAD_X = 5;
const SPARK_PAD_Y = 6;

function TrendSparkline({ trend }: { trend: DetectionTrendPoint[] }) {
  // Unique per-instance id so multiple sparklines don't share/override the
  // same gradient definition.
  const gradientId = useId();

  if (trend.length < 2) {
    return <div className="flex h-full items-center justify-center text-2xs text-muted">—</div>;
  }

  const counts = trend.map((point) => point.count);
  const max = Math.max(...counts);
  const min = Math.min(...counts);
  const flat = max === min;
  const range = flat ? 1 : max - min;

  const x = (index: number) =>
    SPARK_PAD_X + (index / (counts.length - 1)) * (SPARK_W - SPARK_PAD_X * 2);

  const y = (count: number) =>
    flat
      ? SPARK_H / 2 // pin a flat series to the middle rather than the floor
      : SPARK_H - SPARK_PAD_Y - ((count - min) / range) * (SPARK_H - SPARK_PAD_Y * 2);

  const line = counts
    .map((count, index) => `${x(index).toFixed(1)},${y(count).toFixed(1)}`)
    .join(" ");

  const area = `${SPARK_PAD_X},${SPARK_H - SPARK_PAD_Y} ${line} ${SPARK_W - SPARK_PAD_X},${SPARK_H - SPARK_PAD_Y}`;

  // Endpoint marker positioned in % so it stays a perfect circle despite the
  // non-uniform SVG stretch (preserveAspectRatio="none").
  const lastLeft = ((SPARK_W - SPARK_PAD_X) / SPARK_W) * 100;
  const lastTop = (y(counts[counts.length - 1]) / SPARK_H) * 100;

  return (
    <div className="relative h-full w-full">
      <svg
        viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
        preserveAspectRatio="none"
        className="h-full w-full overflow-visible"
        aria-hidden
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity={0.16} />
            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeOpacity={0.95}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current"
        style={{ left: `${lastLeft}%`, top: `${lastTop}%` }}
      />
    </div>
  );
}

function Separator() {
  return <span className="h-3 w-px shrink-0 bg-border/70" />;
}

const DomainRow = memo(function DomainRow({
  entry,
  isActive,
  onSelect,
  actionableCaption,
}: {
  entry: DetectionPerDomainEntry;
  isActive: boolean;
  onSelect: (domain: string) => void;
  actionableCaption: string;
}) {
  const trend = entry.trend ?? [];
  const expected = entry.expected_count ?? null;

  const pct = actionablePct(entry.total_detections, entry.actionable_count);
  const status = getStatus(entry.total_detections, expected);
  const delta = getDeltaPct(entry.total_detections, expected);
  const sparkColor = status ? SPARKLINE_COLOR[status] : "text-muted";

  return (
    <button
      type="button"
      onClick={() => onSelect(entry.domain)}
      aria-pressed={isActive}
      className={[
        `${RANKING_ROW} group text-left transition-colors`,
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus",
        isActive ? "bg-brand-cyan/10" : "hover:bg-surface-elevated",
      ].join(" ")}
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className="min-w-0 truncate text-sm font-semibold leading-5 text-primary"
            title={entry.domain}
          >
            {entry.domain}
          </span>

          <StatusBadge status={status} />

          {isActive && <span className="shrink-0 text-xs font-medium text-primary">Selected</span>}
        </div>

        <div className={RANKING_META}>
          <DeltaText delta={delta} status={status} />

          {delta !== null && <Separator />}

          <span className="shrink-0 tabular-nums">
            {formatCount(entry.actionable_count)} {actionableCaption}
          </span>

          <Separator />

          <div className="flex shrink-0 items-center gap-1.5">
            <span
              role="progressbar"
              aria-label="Actionable share of detections"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-1.5 w-20 overflow-hidden rounded-full bg-background/70"
            >
              <span
                className="block h-full rounded-full bg-brand-cyan/70"
                style={{ width: `${pct}%` }}
              />
            </span>

            <span className="text-2xs tabular-nums text-muted">{pct}%</span>
          </div>
        </div>
      </div>

      <div className="w-20 shrink-0 text-right">
        <span className="text-xl font-semibold leading-none tracking-display text-primary tabular-nums">
          {formatCount(entry.total_detections)}
        </span>
        <span className="sr-only"> total detections</span>
      </div>

      <div className={RANKING_VIZ}>
        <div className={`h-full w-full ${sparkColor}`}>
          <TrendSparkline trend={trend} />
        </div>
      </div>
    </button>
  );
});

function RowSkeleton() {
  return (
    <div className={RANKING_ROW}>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-3.5 w-full max-w-32 rounded skeleton" />
        <div className="h-2.5 w-full max-w-48 rounded skeleton" />
      </div>
      <div className="h-6 w-14 rounded skeleton" />
      <div className={`${RANKING_VIZ} rounded skeleton`} />
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

function DetectionPerDomainComponent({
  data,
  config,
  dashboard,
}: WidgetProps<DetectionPerDomainConfig>) {
  const error = firstError(data, ["domains"]);
  const resolved = unwrap<DetectionPerDomainData>(data.domains);
  const isLoading = data.domains?.isLoading ?? false;

  const title = config.title ?? defaultConfig.title ?? "Detection per Domain";
  const labels = resolveLabels(LABELS, config);
  const maxDomains = config.maxDomains ?? defaultConfig.maxDomains ?? 10;

  const activeDomain = dashboard.filters.dataFilters.find(
    (filter) => filter.field === "detection_domain",
  )?.value as string | undefined;

  const allDomains = resolved?.domains ?? EMPTY_DOMAINS;

  // Sort / slice / total computed once per data change instead of every render.
  const { domains, grandTotal, hiddenCount } = useMemo(() => {
    const sorted = [...allDomains].sort((a, b) => b.total_detections - a.total_detections);
    const top = sorted.slice(0, maxDomains);
    const total = allDomains.reduce((sum, domain) => sum + domain.total_detections, 0);
    return {
      domains: top,
      grandTotal: total,
      hiddenCount: allDomains.length - top.length,
    };
  }, [allDomains, maxDomains]);

  // Keep a stable click handler (empty deps) so memoized rows don't re-render on
  // every poll/refresh. Latest filter state + dashboard are read via refs.
  const activeDomainRef = useRef(activeDomain);
  activeDomainRef.current = activeDomain;
  const dashboardRef = useRef(dashboard);
  dashboardRef.current = dashboard;

  const handleSelect = useCallback((domain: string) => {
    const dash = dashboardRef.current;
    if (activeDomainRef.current === domain) {
      dash.clearFilter({ dataKey: "domains", filterKey: "domain" });
    } else {
      dash.applyFilter({
        dataKey: "domains",
        filterKey: "domain",
        value: domain,
      });
    }
  }, []);

  if (error) {
    console.error("[DetectionPerDomainWidget] Failed to load data:", error);

    return (
      <Widget title={title}>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  const hasData = domains.length > 0;

  // First load (no data yet): show a skeleton rather than a bare spinner.
  if (!hasData && isLoading) {
    const skeletonRows = Math.min(maxDomains, 6);
    return (
      <Widget title={title}>
        <div className="h-full overflow-hidden">
          {Array.from({ length: skeletonRows }, (_, index) => (
            <RowSkeleton key={index} />
          ))}
        </div>
      </Widget>
    );
  }

  if (!hasData) {
    return (
      <Widget title={title}>
        <WidgetState title={labels.emptyTitle} description={labels.emptyDescription} />
      </Widget>
    );
  }

  return (
    <Widget title={title} actions={isLoading ? <UpdatingPill /> : undefined}>
      <div className="@container flex h-full min-h-0 flex-col">
        <WidgetSummary
          value={formatCount(grandTotal)}
          exactValue={grandTotal.toLocaleString()}
          label={labels.summaryLabel}
          description={labels.summaryDescription}
        />
        {activeDomain && (
          <p className="shrink-0 border-b border-border px-3 py-2 text-xs text-muted">
            Filtered on <span className="font-medium text-secondary">{activeDomain}</span> — click
            the highlighted domain to clear.
          </p>
        )}

        <RankingList
          identity={labels.domainHeading}
          metric={labels.detectionsHeading}
          visualization={labels.trendHeading}
        >
          {domains.map((entry) => (
            <DomainRow
              key={entry.domain}
              entry={entry}
              isActive={activeDomain === entry.domain}
              onSelect={handleSelect}
              actionableCaption={labels.actionableCaption}
            />
          ))}
        </RankingList>

        <WidgetFooter>
          <span>{domains.length} domains shown</span>
          {hiddenCount > 0 ? <span>· {hiddenCount} not shown</span> : null}
        </WidgetFooter>
      </div>
    </Widget>
  );
}

export const Component = DetectionPerDomainComponent;

const widgetModule: WidgetModule<DetectionPerDomainConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
